import React, { useState, useEffect, useCallback } from 'react';
import { loadOrCreateIdentity } from '../lib/identity';
import { getFreshFeed, savePin, getPinsForVideo, getUserPins, calculateTastemakerScore, calculatePinScore, VideoMetadata, getFollowingFeed, getRemoteProfile } from '../lib/metadata';
import { recordSignal, getForYouFeed, getTrendingFeed, getLocalFeed } from '../lib/signals';
import { getCity } from '../lib/location';
import { VideoPlayer } from './VideoPlayer';
import { Search } from './Search';
import { publishEvent } from '../lib/sync';
import { asText } from '../lib/sanitize';
import { COLORS, SPACING } from '../styles/theme';
import { useMobile } from '../hooks/useMobile';

const MODES = [
  { mode: 'foryou' as const, label: 'For You' },
  { mode: 'trending' as const, label: 'Trending' },
  { mode: 'following' as const, label: 'Following' },
  { mode: 'local' as const, label: 'Local' },
];

function timeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  return `${Math.floor(s / 2592000)}mo ago`;
}

function UploaderAvatar({ userId }: { userId: string }) {
  const profile = getRemoteProfile(userId);
  const name = asText(profile?.username || userId);
  const letter = name[0]?.toUpperCase() || '?';
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      backgroundColor: COLORS.accent, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '11px', fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace',
      flexShrink: 0,
    }}>
      {letter}
    </div>
  );
}

function VideoCard({
  video, isPinned, isLiked, isDisliked, pinScore, feedMode, whyOpen,
  onPlay, onPin, onLike, onDislike, onProfile, onTag, onToggleWhy,
}: {
  video: VideoMetadata & { algorithmScore?: number; scoreBreakdown?: any };
  isPinned: boolean; isLiked: boolean; isDisliked: boolean; pinScore: number;
  feedMode: string; whyOpen: boolean;
  onPlay: () => void; onPin: () => void; onLike: () => void; onDislike: () => void;
  onProfile: () => void; onTag: (t: string) => void; onToggleWhy: () => void;
}) {
  const profile = getRemoteProfile(video.uploader);
  const username = asText(profile?.username || video.uploader);
  const pinCount = getPinsForVideo(video.hash).length;

  return (
    <div style={{ cursor: 'pointer', userSelect: 'none' }} onClick={onPlay}>
      {/* 16:9 thumbnail */}
      <div style={{
        position: 'relative', width: '100%', paddingTop: '56.25%',
        backgroundColor: '#111', overflow: 'hidden', borderRadius: 2,
      }}>
        {video.thumbnailDataUrl ? (
          <img
            src={video.thumbnailDataUrl}
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: COLORS.textMuted, fontSize: '28px',
          }}>▶</div>
        )}
      </div>

      {/* Card body */}
      <div style={{ display: 'flex', gap: SPACING.sm, padding: `${SPACING.sm} 0 ${SPACING.md}` }}>
        <div style={{ paddingTop: 2 }} onClick={e => { e.stopPropagation(); onProfile(); }}>
          <UploaderAvatar userId={video.uploader} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title */}
          <div style={{
            fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700, fontSize: '13px',
            color: COLORS.text, lineHeight: 1.35, marginBottom: 4,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {video.title}
          </div>

          {/* Channel + meta */}
          <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '11px', color: COLORS.textMuted, marginBottom: 6 }}>
            <button
              onClick={e => { e.stopPropagation(); onProfile(); }}
              style={{ background: 'none', border: 'none', padding: 0, color: COLORS.textMuted, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }}
            >
              {username}
            </button>
            {' · '}{pinCount} pins · {timeAgo(video.uploadedAt)}
          </div>

          {/* Tags */}
          {video.tags && video.tags.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
              {video.tags.slice(0, 3).map((tag: string) => (
                <button
                  key={tag}
                  onClick={e => { e.stopPropagation(); onTag(tag); }}
                  style={{
                    background: 'none', border: `1px solid ${COLORS.border}`,
                    color: COLORS.textMuted, padding: '1px 6px',
                    fontSize: '9px', fontFamily: '"IBM Plex Mono", monospace',
                    cursor: 'pointer', borderRadius: 2,
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: SPACING.xs, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <button
              onClick={onPin} disabled={isPinned}
              style={{
                background: isPinned ? COLORS.panel : COLORS.accent,
                color: isPinned ? COLORS.textMuted : '#fff',
                border: 'none', padding: '3px 10px',
                cursor: isPinned ? 'default' : 'pointer',
                fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', fontWeight: 700,
                borderRadius: 2,
              }}
            >
              {isPinned ? `✓ pinned${pinScore > 0 ? ` +${pinScore}` : ''}` : 'pin'}
            </button>
            <button
              onClick={onLike} disabled={isLiked}
              style={{
                background: isLiked ? COLORS.accent : 'transparent',
                color: isLiked ? '#fff' : COLORS.textMuted,
                border: `1px solid ${COLORS.border}`, padding: '2px 8px',
                cursor: isLiked ? 'default' : 'pointer',
                fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', borderRadius: 2,
              }}
            >
              ▲
            </button>
            <button
              onClick={onDislike} disabled={isDisliked}
              style={{
                background: 'transparent',
                color: isDisliked ? COLORS.accent : COLORS.textMuted,
                border: `1px solid ${COLORS.border}`, padding: '2px 8px',
                cursor: isDisliked ? 'default' : 'pointer',
                fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', borderRadius: 2,
              }}
            >
              ▼
            </button>
            {feedMode === 'foryou' && (video as any).scoreBreakdown && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={onToggleWhy}
                  style={{
                    background: 'transparent', border: `1px solid ${COLORS.border}`,
                    color: COLORS.textMuted, padding: '2px 6px',
                    cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', fontSize: '9px', borderRadius: 2,
                  }}
                >
                  ?
                </button>
                {whyOpen && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, zIndex: 20,
                    background: COLORS.bg, border: `1px solid ${COLORS.accent}`,
                    padding: SPACING.sm, fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: '10px', whiteSpace: 'nowrap', color: COLORS.text, marginBottom: 4,
                  }}>
                    <div>Personal: {((video as any).scoreBreakdown.personalAffinity * 100).toFixed(0)}%</div>
                    <div>Social:   {((video as any).scoreBreakdown.socialSignal * 100).toFixed(0)}%</div>
                    <div>Recency:  {((video as any).scoreBreakdown.recency * 100).toFixed(0)}%</div>
                    <div style={{ marginTop: 4, borderTop: `1px solid ${COLORS.border}`, paddingTop: 4 }}>
                      Score: {((video as any).algorithmScore * 100).toFixed(1)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Feed({ onViewUserProfile, onViewTag }: { onViewUserProfile: (userId: string) => void; onViewTag?: (tag: string) => void }) {
  const [videos, setVideos] = useState<VideoMetadata[]>([]);
  const [feedMode, setFeedMode] = useState<'foryou' | 'trending' | 'following' | 'local'>('foryou');
  const [likedVideos, setLikedVideos] = useState<Set<string>>(new Set());
  const [dislikedVideos, setDislikedVideos] = useState<Set<string>>(new Set());
  const [whyThisHash, setWhyThisHash] = useState<string | null>(null);
  const [identity, setIdentity] = useState<any>(null);
  const [userPins, setUserPins] = useState<Set<string>>(new Set());
  const [selectedVideoHash, setSelectedVideoHash] = useState<string | null>(null);
  const isMobile = useMobile();

  useEffect(() => { setIdentity(loadOrCreateIdentity()); }, []);

  const loadFeed = useCallback(() => {
    if (!identity) return;
    let feed: VideoMetadata[];
    if (feedMode === 'foryou') feed = getForYouFeed();
    else if (feedMode === 'trending') feed = getTrendingFeed();
    else if (feedMode === 'following') feed = getFollowingFeed(identity.userId);
    else {
      const location = getCity();
      feed = location ? getLocalFeed(location.city) : getFreshFeed();
    }
    setVideos(feed);
    setUserPins(new Set(getUserPins(identity.userId).map((p: any) => p.videoHash)));
  }, [feedMode, identity]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const handlePin = async (video: VideoMetadata) => {
    if (!identity) return;
    const pin = { videoHash: video.hash, userId: identity.userId, pinnedAt: Date.now(), signature: '' };
    if (savePin(pin)) {
      await publishEvent('pin', pin);
      await recordSignal(video.hash, 'pin', video.magnetURI);
      setUserPins(prev => new Set(prev).add(video.hash));
      loadFeed();
    }
  };

  const handleLike = async (video: VideoMetadata) => {
    if (likedVideos.has(video.hash)) return;
    await recordSignal(video.hash, 'like', video.magnetURI);
    setLikedVideos(prev => new Set(prev).add(video.hash));
    setDislikedVideos(prev => { const s = new Set(prev); s.delete(video.hash); return s; });
  };

  const handleDislike = async (video: VideoMetadata) => {
    if (dislikedVideos.has(video.hash)) return;
    await recordSignal(video.hash, 'dislike', video.magnetURI);
    setDislikedVideos(prev => new Set(prev).add(video.hash));
    setLikedVideos(prev => { const s = new Set(prev); s.delete(video.hash); return s; });
  };

  if (!identity) return (
    <div style={{ padding: SPACING.xl, fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px', color: COLORS.textMuted }}>
      Loading...
    </div>
  );

  if (selectedVideoHash) {
    return (
      <VideoPlayer
        videoHash={selectedVideoHash}
        onBack={() => setSelectedVideoHash(null)}
        onViewUserProfile={onViewUserProfile}
        onViewTag={onViewTag}
      />
    );
  }

  const tastemakerScore = calculateTastemakerScore(identity.userId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', backgroundColor: COLORS.bg }}>
      {/* Search bar */}
      <Search onSelectVideo={setSelectedVideoHash} />

      {/* Filter chips row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: SPACING.sm,
        padding: `${SPACING.sm} ${SPACING.md}`,
        borderBottom: `1px solid ${COLORS.border}`,
        overflowX: 'auto', flexShrink: 0,
      }}>
        {MODES.map(({ mode, label }) => (
          <button
            key={mode}
            onClick={() => setFeedMode(mode)}
            style={{
              background: feedMode === mode ? COLORS.accent : COLORS.panel,
              color: feedMode === mode ? '#fff' : COLORS.text,
              border: `1px solid ${feedMode === mode ? COLORS.accent : COLORS.border}`,
              borderRadius: 16, padding: '4px 14px',
              cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '11px', fontWeight: feedMode === mode ? 700 : 400,
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            {label}
          </button>
        ))}
        {feedMode === 'local' && (() => {
          const loc = getCity();
          return (
            <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', color: COLORS.textMuted, whiteSpace: 'nowrap' }}>
              {loc ? `⌖ ${loc.city}` : 'set city in Settings'}
            </span>
          );
        })()}
        <div style={{ marginLeft: 'auto', flexShrink: 0, fontFamily: '"IBM Plex Mono", monospace', fontSize: '10px', color: COLORS.accent, fontWeight: 700, whiteSpace: 'nowrap' }}>
          ★ {tastemakerScore}
        </div>
      </div>

      {/* Video grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? `${SPACING.md} ${SPACING.sm}` : SPACING.lg }}>
        {videos.length === 0 ? (
          <div style={{ color: COLORS.textMuted, fontFamily: '"IBM Plex Mono", monospace', fontSize: '12px' }}>
            No videos yet.
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: isMobile ? SPACING.lg : SPACING.xl,
          }}>
            {videos.map((video: any) => (
              <VideoCard
                key={video.hash}
                video={video}
                isPinned={userPins.has(video.hash)}
                isLiked={likedVideos.has(video.hash)}
                isDisliked={dislikedVideos.has(video.hash)}
                pinScore={userPins.has(video.hash) ? calculatePinScore(identity.userId, video.hash) : 0}
                feedMode={feedMode}
                whyOpen={whyThisHash === video.hash}
                onPlay={() => setSelectedVideoHash(video.hash)}
                onPin={() => handlePin(video)}
                onLike={() => handleLike(video)}
                onDislike={() => handleDislike(video)}
                onProfile={() => onViewUserProfile(video.uploader)}
                onTag={tag => onViewTag?.(tag)}
                onToggleWhy={() => setWhyThisHash(whyThisHash === video.hash ? null : video.hash)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
